import { useQuery } from '@tanstack/react-query';
import { creatorbaseQueries } from '@round/api';
import useApplicationManagerProject from 'Hooks/queries/useApplicationManagerProject';
import { useCheckUserGroupsAccess } from 'Modules/Auth/hooks/useCheckUserGroupsAccess';

const creatorbaseUtils = creatorbaseQueries.project.utils;

// hook is only enabled for creatorbase_round_planner group
export function useLinkedProject(projectId: number | null | undefined) {
    const hasLinkToCreatorbaseAccess = useCheckUserGroupsAccess(['creatorbase_round_planner']);
    const {
        data: appManagerProject,
        status: appManagerProjectStatus,
        error: appManagerProjectError,
        utils: appManagerUtils,
    } = useApplicationManagerProject(projectId, { enabled: !!hasLinkToCreatorbaseAccess });

    const {
        data: linkedProject,
        status: linkedProjectStatus,
        error: linkedProjectError,
    } = useQuery({
        ...creatorbaseQueries.project.options.detail(appManagerProject?.creatorbase_project_id, { team_only: false }),
        enabled: !!appManagerProject?.creatorbase_project_id && !!hasLinkToCreatorbaseAccess,
    });

    const {
        data: team,
        status: teamStatus,
        error: teamError,
    } = useQuery({
        ...creatorbaseQueries.team.options.detail(linkedProject?.team_id, { team_only: false }),
        enabled: !!linkedProject?.team_id && !!hasLinkToCreatorbaseAccess,
    });

    const {
        data: brand,
        status: brandStatus,
        error: brandError,
    } = useQuery({
        ...creatorbaseQueries.brand.options.detail(linkedProject?.brand_id, { team_only: false }),
        enabled: !!linkedProject?.brand_id && !!hasLinkToCreatorbaseAccess,
    });

    const hasErrorLoadingSupplementaryData = !!teamError || !!brandError;

    return {
        appManagerProject,
        appManagerProjectStatus,
        appManagerProjectError: appManagerUtils.getErrorResponse(appManagerProjectError),
        linkedProject,
        linkedProjectStatus,
        linkedProjectError: creatorbaseUtils.detail.getErrorResponse(linkedProjectError),
        team,
        brand,

        isLinkedProjectLoading:
            appManagerProjectStatus === 'pending' ||
            (!!appManagerProject?.creatorbase_project_id && linkedProjectStatus === 'pending') ||
            (!!linkedProject?.brand_id && brandStatus === 'pending') ||
            (!!linkedProject?.team_id && teamStatus === 'pending'),
        hasErrorLoadingLinkedProject: !!appManagerProjectError || !!linkedProjectError,
        hasErrorLoadingSupplementaryData,
    };
}
